import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * A custom element that displays the cart total and item count in the header.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} summaryText - The element containing the formatted summary text.
 *
 * @extends {Component<Refs>}
 */
class HeaderCartSummary extends Component {
  requiredRefs = ['summaryText'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.renderSummary(this.initialTotalPrice, this.initialItemCount);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  /**
   * Handles cart update events.
   * @param {CustomEvent<{ resource?: { total_price?: number, item_count?: number } }>} event
   */
  onCartUpdate = (event) => {
    const totalPrice = event.detail.resource?.total_price;
    const itemCount = event.detail.resource?.item_count;
    if (typeof totalPrice !== 'number' || typeof itemCount !== 'number') return;

    this.renderSummary(totalPrice, itemCount);
  };

  /**
   * Renders the cart summary text.
   * @param {number} totalPrice
   * @param {number} itemCount
   */
  renderSummary(totalPrice, itemCount) {
    this.refs.summaryText.textContent = `${this.formatTotalPrice(totalPrice)} (${itemCount})`;
  }

  /**
   * @param {number} totalPrice
   * @returns {string}
   */
  formatTotalPrice(totalPrice) {
    return formatMoney(totalPrice, this.moneyFormat, this.currency);
  }

  /** @returns {number} */
  get initialTotalPrice() {
    return Number(this.dataset.totalPrice ?? '0');
  }

  /** @returns {number} */
  get initialItemCount() {
    return Number(this.dataset.itemCount ?? '0');
  }

  /** @returns {string} */
  get moneyFormat() {
    return this.dataset.moneyFormat ?? '{{amount_with_comma_separator}} EUR';
  }

  /** @returns {string} */
  get currency() {
    return this.dataset.currency ?? 'EUR';
  }
}

if (!customElements.get('header-cart-summary')) {
  customElements.define('header-cart-summary', HeaderCartSummary);
}
